export const MIN_PHONE_DIGITS = 7;
/** Minimum digit count for SMS-quality E.164 (national mobiles). */
export const MIN_SMS_PHONE_DIGITS = 10;
export const MAX_PHONE_DIGITS = 15;
export const DEFAULT_SMS_COUNTRY_CODE = "256";
/** Uganda mobiles are 07xx xxx xxx → E.164 +2567XXXXXXXX (12 digits). */
const UG_MOBILE_E164 = /^\+2567\d{8}$/;
export function phoneDigits(value) {
    return (value ?? "").replace(/\D/g, "");
}
/**
 * Convert a guest-entered mobile into E.164 for Africa's Talking.
 * National numbers that start with 0 (or are short local mobiles) use
 * `defaultCountryCode` (Uganda 256 by default).
 *
 * Length floor here stays at {@link MIN_PHONE_DIGITS} so normalisation remains
 * permissive; SMS send/save paths must also pass {@link isValidSmsPhone}.
 */
export function toE164(value, defaultCountryCode = DEFAULT_SMS_COUNTRY_CODE) {
    const raw = (value ?? "").trim();
    if (!raw)
        return null;
    const country = phoneDigits(defaultCountryCode) || DEFAULT_SMS_COUNTRY_CODE;
    let digits = phoneDigits(raw);
    if (!digits)
        return null;
    if (raw.startsWith("+")) {
        if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS)
            return null;
        return `+${digits}`;
    }
    if (digits.startsWith("0") && digits.length > 1) {
        digits = country + digits.slice(1);
    }
    else if (!digits.startsWith(country) && digits.length <= 9) {
        digits = country + digits;
    }
    if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS)
        return null;
    return `+${digits}`;
}
/**
 * True when `value` normalises to an SMS-quality E.164 mobile.
 * - Uganda (`+256…`): must be `+2567` + 8 digits (12 total).
 * - Other countries: 10–15 digits, leading `+`, reject all-identical digit junk.
 */
export function isValidSmsPhone(value, defaultCountryCode = DEFAULT_SMS_COUNTRY_CODE) {
    const e164 = toE164(value, defaultCountryCode);
    if (!e164)
        return false;
    const digits = e164.slice(1);
    if (digits.length < MIN_SMS_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
        return false;
    }
    // Obvious junk: +11111111111, +0000000000, etc.
    if (/^(\d)\1+$/.test(digits))
        return false;
    if (digits.startsWith("256")) {
        return UG_MOBILE_E164.test(e164);
    }
    return /^\+\d{10,15}$/.test(e164);
}
/** Normalise and accept only SMS-quality mobiles; otherwise null. */
export function toSmsE164(value, defaultCountryCode = DEFAULT_SMS_COUNTRY_CODE) {
    const e164 = toE164(value, defaultCountryCode);
    if (!e164 || !isValidSmsPhone(e164, defaultCountryCode))
        return null;
    return e164;
}
export function uniqueE164Numbers(values, defaultCountryCode = DEFAULT_SMS_COUNTRY_CODE) {
    const seen = new Set();
    const out = [];
    for (const value of values) {
        const e164 = toSmsE164(value, defaultCountryCode);
        if (!e164 || seen.has(e164))
            continue;
        seen.add(e164);
        out.push(e164);
    }
    return out;
}
//# sourceMappingURL=phone.js.map